import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import client from '../../api/client';
import schedulingApi from '../../api/scheduling';
import usersApi from '../../api/users';
import useAuth from '../../hooks/useAuth';
import { isSSLOrAbove } from '../../utils/roles';
import ScheduleCalendar from './ScheduleCalendar';
import ShiftEditorModal from './ShiftEditorModal';

// ── API helpers ───────────────────────────────────────────────────────────────

function fetchSchedules(periodId, locationId) {
  const params = [];
  if (periodId)  params.push(`period=${periodId}`);
  if (locationId) params.push(`location=${locationId}`);
  return client.get(`/schedules/?${params.join('&')}`);
}

// ── Period selector (SSL/Admin only) ─────────────────────────────────────────

function PeriodSelector({ periods, selectedId, onChange }) {
  return (
    <div className="flex items-center gap-3">
      <label className="text-sm font-medium text-gray-600">Period:</label>
      <select
        value={selectedId ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        <option value="">— Select period —</option>
        {periods.map((p) => (
          <option key={p.id} value={p.id}>
            {p.start_date} → {p.end_date} ({p.status})
          </option>
        ))}
      </select>
    </div>
  );
}

// ── Location tabs ─────────────────────────────────────────────────────────────

function LocationTabs({ locations, activeId, onChange }) {
  return (
    <div className="flex gap-2">
      {locations.map((loc) => (
        <button
          key={loc.id}
          onClick={() => onChange(loc.id)}
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            activeId === loc.id
              ? 'bg-indigo-600 text-white'
              : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          {loc.name}
        </button>
      ))}
    </div>
  );
}

// ── Publish banner ────────────────────────────────────────────────────────────

function PublishBanner({ schedule, onPublish, isPublishing }) {
  if (!schedule || schedule.status !== 'DRAFT') return null;
  return (
    <div className="flex items-center justify-between rounded-xl border border-yellow-200 bg-yellow-50 px-4 py-3">
      <p className="text-sm text-yellow-800">
        This schedule is a <strong>draft</strong>. Review assignments, then publish to make it visible to SLs.
      </p>
      <button
        onClick={onPublish}
        disabled={isPublishing}
        className="ml-4 flex-shrink-0 rounded-lg bg-yellow-500 px-4 py-2 text-sm font-semibold text-white hover:bg-yellow-600 disabled:opacity-40 transition-colors"
      >
        {isPublishing ? 'Publishing…' : 'Publish Schedule'}
      </button>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SchedulePage({ manage = false }) {
  const { user }    = useAuth();
  const queryClient = useQueryClient();
  const isSSL       = isSSLOrAbove(user);

  const [selectedPeriodId, setSelectedPeriodId] = useState(null);
  const [activeLocationId, setActiveLocationId] = useState(null);
  const [editingDay,       setEditingDay]       = useState(null);

  // ── Locations ──────────────────────────────────────────────────────────────

  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn:  usersApi.getLocations,
  });

  useEffect(() => {
    if (locations.length && !activeLocationId) {
      setActiveLocationId(locations[0].id);
    }
  }, [locations, activeLocationId]);

  // ── Periods (SSL+ only) ────────────────────────────────────────────────────

  const { data: periods = [] } = useQuery({
    queryKey: ['periods'],
    queryFn:  schedulingApi.getPeriods,
    enabled:  isSSL,
  });

  useEffect(() => {
    if (!periods.length || selectedPeriodId) return;
    const recent = [...periods]
      .sort((a, b) => new Date(b.start_date) - new Date(a.start_date))
      .find((p) => ['OPEN', 'CLOSED', 'GENERATED', 'PUBLISHED'].includes(p.status));
    if (recent) setSelectedPeriodId(String(recent.id));
  }, [periods, selectedPeriodId]);

  // ── Schedule list (filter by period + location) ────────────────────────────

  const scheduleListKey = ['schedules', selectedPeriodId, activeLocationId];

  const { data: scheduleList = [], isLoading: listLoading } = useQuery({
    queryKey: scheduleListKey,
    queryFn:  () => fetchSchedules(selectedPeriodId, activeLocationId),
    enabled:  !!activeLocationId && (isSSL ? !!selectedPeriodId : true),
  });

  const scheduleStub = Array.isArray(scheduleList) ? scheduleList[0] : null;

  // ── Schedule detail (full with assignments) ────────────────────────────────

  const scheduleDetailKey = ['schedule-detail', scheduleStub?.id];

  const { data: scheduleDetail, isLoading: detailLoading } = useQuery({
    queryKey: scheduleDetailKey,
    queryFn:  () => schedulingApi.getSchedule(scheduleStub.id),
    enabled:  !!scheduleStub?.id,
  });

  const activeSchedule = scheduleDetail ?? scheduleStub;
  const isLoading      = listLoading || detailLoading;

  // ── Publish ────────────────────────────────────────────────────────────────

  const publishMutation = useMutation({
    mutationFn: () => schedulingApi.publishSchedule(activeSchedule.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleListKey });
      queryClient.invalidateQueries({ queryKey: scheduleDetailKey });
    },
  });

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-bold text-gray-900">
        {manage ? 'Manage Schedule' : 'Duty Schedule'}
      </h1>

      {isSSL && (
        <PeriodSelector
          periods={periods}
          selectedId={selectedPeriodId}
          onChange={setSelectedPeriodId}
        />
      )}

      {locations.length > 0 && (
        <LocationTabs
          locations={locations}
          activeId={activeLocationId}
          onChange={setActiveLocationId}
        />
      )}

      {(manage || isSSL) && (
        <PublishBanner
          schedule={activeSchedule}
          onPublish={() => publishMutation.mutate()}
          isPublishing={publishMutation.isPending}
        />
      )}

      {publishMutation.isSuccess && (
        <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
          Schedule published — SLs can now see it.
        </div>
      )}

      {isLoading && (
        <p className="text-sm text-gray-400 text-center py-12">Loading schedule…</p>
      )}

      {!isLoading && !activeSchedule && (
        <div className="rounded-xl border border-dashed border-gray-200 bg-white p-12 text-center text-gray-400">
          {isSSL && !selectedPeriodId
            ? 'Select a period above to view the schedule.'
            : 'No schedule available yet.'}
        </div>
      )}

      {!isLoading && activeSchedule && (
        <ScheduleCalendar
          schedule={activeSchedule}
          isManage={manage || isSSL}
          onDayClick={(date) => setEditingDay(date)}
        />
      )}

      {editingDay && activeSchedule && (
        <ShiftEditorModal
          date={editingDay}
          schedule={activeSchedule}
          onClose={() => setEditingDay(null)}
        />
      )}
    </div>
  );
}
